import { Grid, Paper, Typography, Box, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { formatCurrencyShort, formatShortNumber } from "../../../utils/format";
import { useDashboard } from "../../../contexts/DashboardContext";
import { RiArrowRightUpLine } from "react-icons/ri";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 8,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const MetricTitle = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "rgba(0, 0, 0, 0.87)",
});

const STATUS_DOT_COLORS: Record<string, string> = {
  Briefing: "#B0BEC5",
  "Em Aprovação": "#FFA726",
  Aprovado: "#ffffff",
  Fechamento: "#42A5F5",
  Auditado: "#EC407A",
};

const MetricValue = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: 700,
  lineHeight: 1.2,
});

const ComparativeRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "0.75rem",
});

export const DashboardSummary = () => {
  const {
    initialSummary,
    filteredSummary,
    loading,
    selectedDashboardType,
    hasFilters,
    eventControlMonthlyCards,
  } = useDashboard();

  const baseData = hasFilters ? filteredSummary : initialSummary;

  // filteredSummary já contém os valores corretos de event-control/summary (com filtros aplicados)
  const data = baseData;
  const showComparatives =
    (selectedDashboardType === "Supervisor" || selectedDashboardType === "CEO") && !hasFilters;

  const totalMMsDisplay = data?.totalMMs ?? 0;

  const MONTH_LABELS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

  const MonthlyGrid = ({ months }: { months: { key: string; label: string; value: number }[] }) => {
    const left = months.slice(0, 6);
    const right = months.slice(6, 12);
    return (
      <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, borderTop: "1px solid rgba(255,255,255,0.15)", pt: 1.5 }}>
        {[left, right].map((half, hi) => (
          <Box key={hi} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.4 }}>
            {half.map((m) => (
              <Box key={m.key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.65)", letterSpacing: 0.3 }}>
                  {m.label.replace(".", "").replace(/^\w/, (c) => c.toUpperCase())}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: m.value > 0 ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.35)" }}>
                  {m.value}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Grid container spacing={2} alignItems="stretch">
      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <StyledPaper elevation={0} sx={{ bgcolor: "#5b45d4", color: "white", width: "100%", overflow: "hidden" }}>
          {loading ? (
            <>
              <Skeleton variant="rectangular" width={140} height={16} sx={{ bgcolor: "rgba(255,255,255,0.3)", mb: 1 }} />
              <Skeleton variant="rectangular" width={120} height={32} sx={{ bgcolor: "rgba(255,255,255,0.3)", mb: 1 }} />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={16} sx={{ bgcolor: "rgba(255,255,255,0.2)", mb: 0.5 }} />
              ))}
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <MetricTitle sx={{ color: "rgba(255,255,255,0.85)" }}>Faturamento previsto</MetricTitle>
                <DashboardSourceInfo color="rgba(255,255,255,0.85)" context="Faturamento previsto" />
              </Box>
              <MetricValue sx={{ mb: 1 }}>{formatCurrencyShort(data?.expectedBilling || 0)}</MetricValue>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {(data?.phases ?? []).map((row) => (
                  <Box key={row.phase}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_DOT_COLORS[row.phase] ?? "#fff", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{row.phase}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: "10px", px: 0.75, py: 0.2, minWidth: 22, textAlign: "center" }}>
                          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.9)", lineHeight: 1.4 }}>{row.count}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.9)", minWidth: 64, textAlign: "right" }}>{formatCurrencyShort(row.billing)}</Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)", minWidth: 40, textAlign: "right" }}>{row.billingPercentage.toFixed(1)}%</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ height: 3, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${row.billingPercentage}%`, bgcolor: STATUS_DOT_COLORS[row.phase] ?? "rgba(255,255,255,0.6)", borderRadius: 2 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </StyledPaper>
      </Grid>

      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <StyledPaper elevation={0} sx={{ bgcolor: "#1e9e6a", color: "white", width: "100%", overflow: "hidden" }}>
          {loading ? (
            <>
              <Skeleton variant="rectangular" width={140} height={16} sx={{ bgcolor: "rgba(255,255,255,0.3)", mb: 1 }} />
              <Skeleton variant="rectangular" width={120} height={32} sx={{ bgcolor: "rgba(255,255,255,0.3)", mb: 1 }} />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={16} sx={{ bgcolor: "rgba(255,255,255,0.2)", mb: 0.5 }} />
              ))}
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <MetricTitle sx={{ color: "rgba(255,255,255,0.85)" }}>Receitas previstas</MetricTitle>
                <DashboardSourceInfo color="rgba(255,255,255,0.85)" context="Receitas previstas" />
              </Box>
              <MetricValue sx={{ mb: 1 }}>{formatCurrencyShort(data?.expectedRevenue || 0)}</MetricValue>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {(data?.phases ?? []).map((row) => (
                  <Box key={row.phase}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_DOT_COLORS[row.phase] ?? "#fff", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{row.phase}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: "10px", px: 0.75, py: 0.2, minWidth: 22, textAlign: "center" }}>
                          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.9)", lineHeight: 1.4 }}>{row.count}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.9)", minWidth: 64, textAlign: "right" }}>{formatCurrencyShort(row.revenue)}</Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)", minWidth: 40, textAlign: "right" }}>{row.revenuePercentage.toFixed(1)}%</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ height: 3, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${row.revenuePercentage}%`, bgcolor: STATUS_DOT_COLORS[row.phase] ?? "rgba(255,255,255,0.6)", borderRadius: 2 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </StyledPaper>
      </Grid>

      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <StyledPaper elevation={0} sx={{ background: "linear-gradient(135deg, #3964CD 0%, #4674DF 100%)", color: "white", width: "100%", justifyContent: "center" }}>
          {loading ? (
            <>
              <Skeleton
                variant="rectangular"
                width={120}
                height={32}
                sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
              />
              <Skeleton
                variant="rectangular"
                width={80}
                height={20}
                sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
              />
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <MetricTitle sx={{ color: "rgba(255,255,255,0.85)" }}>MMs</MetricTitle>
                <DashboardSourceInfo color="rgba(255,255,255,0.85)" context="MMs (pipeline de projetos)" />
              </Box>
              <MetricValue>{totalMMsDisplay}</MetricValue>
              {showComparatives && data?.totalMMsGrowthPercentage !== undefined && (
                <ComparativeRow>
                  <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
                    +{data.totalMMsGrowthPercentage}%
                    {data.totalMMsGrowthPercentage > 0 ? <RiArrowRightUpLine size={12} /> : null}
                  </Box>
                  {" | "}
                  <Box component="span">+ {data.totalMMsGrowth || 0} hoje</Box>
                </ComparativeRow>
              )}
              {eventControlMonthlyCards?.mms?.months && (
                <MonthlyGrid months={eventControlMonthlyCards.mms.months} />
              )}
            </>
          )}
        </StyledPaper>
      </Grid>

      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <StyledPaper elevation={0} sx={{ background: "linear-gradient(135deg, #d24e87 0%, #e86fa3 100%)", color: "white", width: "100%", justifyContent: "center" }}>
          {loading ? (
            <>
              <Skeleton
                variant="rectangular"
                width={120}
                height={32}
                sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
              />
              <Skeleton
                variant="rectangular"
                width={80}
                height={20}
                sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
              />
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <MetricTitle sx={{ color: "rgba(255,255,255,0.85)" }}>Auditados</MetricTitle>
                <DashboardSourceInfo color="rgba(255,255,255,0.85)" context="Projetos auditados" hideExpectedNote />
              </Box>
              <MetricValue>{data?.totalAudited || 0}</MetricValue>
              {showComparatives && data?.totalAuditedGrowthPercentage !== undefined && (
                <ComparativeRow>
                  <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
                    +{data.totalAuditedGrowthPercentage}%
                    {data.totalAuditedGrowthPercentage > 0 ? (
                      <RiArrowRightUpLine size={12} />
                    ) : null}
                  </Box>
                  {" | "}
                  <Box component="span">+ {data.totalAuditedGrowth || 0} hoje</Box>
                </ComparativeRow>
              )}
              {eventControlMonthlyCards?.audited?.months && (
                <MonthlyGrid months={eventControlMonthlyCards.audited.months} />
              )}
            </>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};
